import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { createStore, applyMiddleware } from "redux";
import { StoreContext } from "redux-react-hook";
import createSagaMiddleware from "redux-saga";
import { Route, Switch, Redirect } from "react-router-dom";
import { createBrowserHistory } from "history";
import { connectRouter, routerMiddleware } from "connected-react-router";
import { LocalizeProvider, withLocalize } from "react-localize-redux";
import { ConnectedRouter } from "connected-react-router";
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';
import { Helmet } from "react-helmet";

import Home from "./components/pages/Home";
import Login from "./components/pages/Login";
import Register from "./components/pages/Register";
import CreateProfile from "./components/pages/CreateProfile";
import RequiredProfile from "./components/pages/RequiredProfile";
import SuccessPage from "./components/pages/SuccessPage";
import ShareCardPage from "./components/pages/ShareCardPage";
import ConfirmNumberPage from "./components/pages/ConfirmNumberPage";
import ConfirmAccountRecoveryPage from "./components/pages/ConfirmAccountRecoveryPage";
import ContactCardPage from "./components/pages/ContactCardPage";
import ConfirmCodePage from "./components/pages/ConfirmCodePage";
import AccountRecoveryMethodPage from "./components/pages/AccountRecoveryMethodPage";
import ChangePasswordPage from "./components/pages/ChangePasswordPage";
import PrivacyPage from "./components/pages/PrivacyPage";
import ResetPasswordPage from "./components/pages/ResetPasswordPage";
import RecoverAccountPage from "./components/pages/RecoverAccountPage";
import RecoverAccountByEmailPage from "./components/pages/RecoverAccountByEmailPage";
import RecoverAccountByTextPage from "./components/pages/RecoverAccountByTextPage";
import ToastContainerWrapper from "./components/Toasts";
import reducers from "./redux/reducers";
import rootSaga from "./redux/sagas";
import "./styles/index.scss";
import { ALL_LANGUAGES } from "./languageData/constants/languages";
import { getTranslations } from "./languageData";
import { renderToStaticMarkup } from "react-dom/server";
import { clearToken } from './services/auth.service';
import 'polyfill-array-includes';

import * as Sentry from '@sentry/react';
import LogRocket from 'logrocket';
import { LOGROCKET_APP_ID, SENTRY_URL } from "./app.config";
import { DataErasurePage } from "./components/pages/DataErasurePage/DataErasurePage";

const loggerConfig = {
  collapsed: true
};

const setupDevTools = () => {
  return (
    window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()
  );
};

const history = createBrowserHistory();

// clear tokens on load (TEMP WORK AROUND)
clearToken();

// custom middleware: send *_FAILURE actions to Sentry
const sentryMiddleware = store => next => action => {
  if (action.type && action.type.endsWith("_FAILURE")) {
    Sentry.captureMessage(`Redux failure: ${action.type}`, "error");

    if (action.error) {
      Sentry.captureException(action.error);
    }
  }
  return next(action);
};

const sagaMiddleware = createSagaMiddleware({
   onError: (error) => {
    Sentry.captureException(error);
  },
});

const middlewares = []
middlewares.push(routerMiddleware(history));
middlewares.push(sagaMiddleware);
if (process.env.NODE_ENV === 'dev') {
  const { createLogger } = require('redux-logger');
  middlewares.push(createLogger(loggerConfig));
}
middlewares.push(LogRocket.reduxMiddleware());
middlewares.push(sentryMiddleware);

const createStoreWithMiddleware = applyMiddleware(
  ...middlewares
)(createStore);

const store = createStoreWithMiddleware(
  connectRouter(history)(reducers),
  setupDevTools()
);

sagaMiddleware.run(rootSaga);

class App extends React.Component {
  constructor(props) {
    super(props);

    this.props.initialize({
      languages: ALL_LANGUAGES,
      translation: getTranslations(),
      options: { renderToStaticMarkup }
    });
  }

  render() {
    return (
      <React.Fragment>
        <Helmet>
          <script
            async
            src="https://www.googletagmanager.com/gtag/js?id=G-WBJ67KWC5S" >
          </script>
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-WBJ67KWC5S');
            `}
          </script>
        </Helmet>
        <ToastContainerWrapper />
        <Switch>
          <Route path="/login" component={Login} />
          <Route path="/register" component={Register} />
          <Route
            path="/account-recovery-method"
            component={AccountRecoveryMethodPage}
          />
          <Route
            path="/account-recovery-by-email"
            component={RecoverAccountByEmailPage}
          />
          <Route
            path="/account-recovery-by-text"
            component={RecoverAccountByTextPage}
          />
          <Route path="/password-reset" component={ResetPasswordPage} />
          <Route path="/change-password" component={ChangePasswordPage} />
          <Route path="/confirm-number" component={ConfirmNumberPage} />
          <Route
            path="/confirm-account-recovery"
            component={ConfirmAccountRecoveryPage}
          />
          <Route
            path="/confirm-verification-code"
            component={ConfirmCodePage}
          />
          <Route path="/contact-card/:slug" component={ContactCardPage} />
          <Route path="/create-profile" component={CreateProfile} />
          <Route path="/privacy" component={PrivacyPage} />
          <Route path="/data-erasure" component={DataErasurePage} />
          <Route path="/required-profile" component={RequiredProfile} />
          <Route path="/registration-success" component={SuccessPage} />
          <Route path="/recover-account" component={RecoverAccountPage} />
          <Route path="/share-card" component={ShareCardPage} />
          <Route path="/logout" render={() => <Redirect to="/login" />} />
          <Route path="/" component={Home} />
          {/*<Route path="/" render={props => {*/}
          {/*if (localStorage.getItem('token') && localStorage.getItem('profile_id')) {*/}
          {/*return <Home />;*/}
          {/*} else {*/}
          {/*return <Redirect to={{ pathname: '/login', state: { from: props.location } }} />;*/}
          {/*}*/}
          {/*}}/>*/}
          <Route render={() => <Redirect to="/" />} />
        </Switch>
      </React.Fragment>
    );
  }
}

const LocalizedApp = withLocalize(App);

LogRocket.init(LOGROCKET_APP_ID);


// capture console.error and console.warn into Sentry
const origError = console.error;
console.error = (...args) => {
  Sentry.captureMessage(args.map(a => (a instanceof Error ? a.message : a)).join(" "), "error");
  args.forEach(a => {
    if (a instanceof Error) {
      Sentry.captureException(a);
    }
  });
  origError.apply(console, args);
};

const origWarn = console.warn;
console.warn = (...args) => {
  Sentry.captureMessage(args.join(" "), "warning");
  origWarn.apply(console, args);
};

Sentry.init({
  dsn: "https://ca9a3abcab93478e85b4b8a5a7fde554@o256970.ingest.us.sentry.io/1449391",
  tunnel: "/tunnel",
  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
  sendDefaultPii: true,
  environment: process.env.NODE_ENV,
  release: "myhellohello-spa@0.1.0",
  integrations: [
  ],

});

LogRocket.getSessionURL(sessionURL => {
  Sentry.configureScope(scope => {
    scope.setExtra("sessionURL", sessionURL);
  });
});

ReactDOM.render(
  <Provider store={store}>
    <StoreContext.Provider value={store}>
      <PersistGate loading={null} persistor={persistStore(store)}>
        <LocalizeProvider store={store}>
          <ConnectedRouter history={history}>
            <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>}>
              <LocalizedApp />
            </Sentry.ErrorBoundary>
          </ConnectedRouter>
        </LocalizeProvider>
      </PersistGate>
    </StoreContext.Provider>
  </Provider>,
  document.querySelector("#root-container")
);



// WEBPACK FOOTER //
// ./src/index.js